import * as React from "react";

/**
 * Distance (px) from the bottom that still counts as at the bottom, so a
 * sub-pixel scroll offset (zoomed pages) doesn't read as the reader leaving.
 */
const AT_BOTTOM_TOLERANCE_PX = 4;

/** Whether the container's content is taller than its viewport, beyond the tolerance. */
function hasOverflow(container: HTMLElement) {
  return (
    container.scrollHeight - container.clientHeight > AT_BOTTOM_TOLERANCE_PX
  );
}

/**
 * Result returned by useFollowBottom hook.
 */
export interface UseFollowBottomResult {
  /** Whether the container keeps itself scrolled to the bottom as content grows */
  isFollowing: boolean;

  /** Scroll to the bottom and follow new content from then on */
  followBottom: (behavior?: ScrollBehavior) => void;

  /**
   * Stop following for a programmatic jump up the thread. Call it after the
   * jump's own layout change (e.g. expanding a section) has committed, from a
   * layout effect, so that change's resize doesn't pin the view back to the
   * bottom over the jump's scroll. Until the view leaves the bottom, the
   * reader wheels, touches, presses a pointer or a key in the container, or
   * `followBottom` runs, being at the bottom does not resume following. Does
   * nothing while nothing overflows the container, which always follows.
   */
  stopFollowing: () => void;
}

/**
 * Hook that keeps a scroll container scrolled to the bottom while its content
 * grows, the way a chat follows a streaming reply.
 *
 * The container follows from mount, so it opens at the bottom and stays there
 * as late-rendering content (markdown, images, charts) lands. A scroll that
 * moves the viewport up and away from the bottom stops following; scrolling
 * back to the bottom, or calling `followBottom`, resumes it. Content growth
 * alone never stops following; only an upward scroll does, the reader's or a
 * programmatic jump's. A jump that also resizes the content (expanding a task
 * section) must call `stopFollowing` once that change has committed, or its
 * resize pins the view back to the bottom over the jump's scroll. While
 * nothing overflows the container there is nowhere to scroll, so it always
 * follows.
 *
 * @param scrollContainerRef - Ref to the scrollable viewport
 * @param contentRef - Ref to the element whose size changes as content arrives
 * @param options.onContentResize - Runs on every content or viewport resize,
 *   before the pin, so anything sized from the content (the hoist spacer) is
 *   current when the view scrolls to the bottom. Must be stable (memoized): a
 *   new function re-creates the observers.
 */
export function useFollowBottom(
  scrollContainerRef: React.RefObject<HTMLElement>,
  contentRef: React.RefObject<HTMLElement>,
  { onContentResize }: { onContentResize?: () => void } = {},
): UseFollowBottomResult {
  const [isFollowing, setIsFollowing] = React.useState(true);
  // The observers below outlive renders, so they read the latest value here.
  const isFollowingRef = React.useRef(true);
  // Set by stopFollowing until the view has left the bottom, the reader gives
  // input (wheel, touch, key, pointer) in the container, or following resumes
  // another way (followBottom, nothing left to scroll). A jump's own layout
  // change can make the browser's scroll anchoring fire a scroll event while
  // the view is still at the bottom, which must not resume following;
  // anchoring never comes with reader input.
  const ignoreBottomRef = React.useRef(false);
  // Scroll position as of the last scroll event or programmatic scroll (for a
  // smooth one, where it starts). An instant scroll's event only arrives next
  // frame, coalesced with any reader scroll in between, so it must be recorded
  // when it happens or a reader scrolling up in that frame would read as
  // scrolling down.
  const lastScrollTopRef = React.useRef(0);

  const setFollowing = React.useCallback(function setFollowing(
    following: boolean,
  ) {
    isFollowingRef.current = following;
    setIsFollowing(following);
  }, []);

  React.useLayoutEffect(
    function followContentGrowth() {
      const container = scrollContainerRef.current;
      const content = contentRef.current;
      if (!container || !content) {
        return;
      }

      lastScrollTopRef.current = container.scrollTop;
      function trackReaderScroll() {
        const distanceFromBottom =
          container!.scrollHeight -
          container!.scrollTop -
          container!.clientHeight;
        if (distanceFromBottom <= AT_BOTTOM_TOLERANCE_PX) {
          if (!ignoreBottomRef.current) {
            setFollowing(true);
          }
        } else {
          ignoreBottomRef.current = false;
          if (container!.scrollTop < lastScrollTopRef.current) {
            setFollowing(false);
          }
        }
        lastScrollTopRef.current = container!.scrollTop;
      }

      // Fires once on observe (the initial pin) and on every size change of
      // the content (streaming, late layout) or viewport (input box resize).
      // The initial pin is instant so a thread opens at the bottom; later pins
      // glide, so a block that lands mid-stream doesn't jerk the view.
      let hasPinned = false;
      const observer = new ResizeObserver(function pinToBottom() {
        onContentResize?.();
        if (!hasOverflow(container) && !isFollowingRef.current) {
          // Nowhere to scroll, so no scroll event would ever resume following.
          ignoreBottomRef.current = false;
          setFollowing(true);
        }
        if (isFollowingRef.current) {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: hasPinned ? "smooth" : "instant",
          });
          lastScrollTopRef.current = container.scrollTop;
        }
        hasPinned = true;
      });

      function acceptReaderInput() {
        ignoreBottomRef.current = false;
      }
      const readerInputEvents = [
        "wheel",
        "touchstart",
        "keydown",
        "pointerdown",
      ] as const;

      container.addEventListener("scroll", trackReaderScroll, {
        passive: true,
      });
      for (const type of readerInputEvents) {
        container.addEventListener(type, acceptReaderInput, { passive: true });
      }
      observer.observe(content);
      observer.observe(container);
      return () => {
        container.removeEventListener("scroll", trackReaderScroll);
        for (const type of readerInputEvents) {
          container.removeEventListener(type, acceptReaderInput);
        }
        observer.disconnect();
      };
    },
    [scrollContainerRef, contentRef, setFollowing, onContentResize],
  );

  const followBottom = React.useCallback(
    function followBottom(behavior: ScrollBehavior = "instant") {
      ignoreBottomRef.current = false;
      setFollowing(true);
      const container = scrollContainerRef.current;
      if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior });
        lastScrollTopRef.current = container.scrollTop;
      }
    },
    [scrollContainerRef, setFollowing],
  );

  const stopFollowing = React.useCallback(
    function stopFollowing() {
      const container = scrollContainerRef.current;
      // Nowhere to scroll: the jump can't move the view, and stopping would
      // leave the "Scroll to bottom" pill up until the next resize.
      if (container && !hasOverflow(container)) {
        return;
      }
      ignoreBottomRef.current = true;
      setFollowing(false);
    },
    [scrollContainerRef, setFollowing],
  );

  return { isFollowing, followBottom, stopFollowing };
}
